import { uniqueId } from 'lodash'
import moment from 'moment'
import { DATE_FORMAT, MONTH_NAME_AND_DATE_WITH_YEAR_FORMAT } from '@src/config/momentFormat'
import { EventsItemType, Measurement } from '@src/screens/Events/models/events.types'
import { HealthDataMeasurementTypeDef } from '@src/types'

const buildSection = (
  date: string,
  index: number,
  initialItem: EventsItemType,
  adornment?: React.ReactNode,
) => ({
  id: uniqueId(date),
  index,
  title: moment(date).format(MONTH_NAME_AND_DATE_WITH_YEAR_FORMAT),
  occurredAt: moment(date),
  data: [initialItem],
  adornment,
})

export const buildSections = ({
  items,
  measurementTypeDefLookup,
  moveDailyMeasurementsToEnd = false,
  firstSectionAdornment = null,
}: {
  items: EventsItemType[]
  measurementTypeDefLookup?: {
    [key: string]: HealthDataMeasurementTypeDef | undefined
  }
  moveDailyMeasurementsToEnd?: boolean
  firstSectionAdornment?: React.ReactNode
}) => {
  if (items.length === 0) {
    return []
  }

  let item = items[0]
  let itemDate = moment(item.occurredAt).format(DATE_FORMAT)

  let sectionIndex = 0

  const sections = [buildSection(itemDate, sectionIndex, item, firstSectionAdornment)]

  for (let index = 1; index < items.length; index += 1) {
    item = items[index]
    const newItemDate = moment(item.occurredAt).format(DATE_FORMAT)
    if (newItemDate !== itemDate) {
      // push new section
      itemDate = newItemDate
      sectionIndex += 1
      sections.push(buildSection(itemDate, sectionIndex, item))
    } else {
      // add item to existing section
      sections[sectionIndex].data.push(item)
    }
  }

  // Move daily measurements to the end of each section
  if (measurementTypeDefLookup && moveDailyMeasurementsToEnd) {
    sections.forEach((section) => {
      section.data.sort((a, b) => {
        const aIsMeasurement = a.__typename === 'Measurement'
        const bIsMeasurement = b.__typename === 'Measurement'

        const aIsDailyMeasurement = aIsMeasurement
          ? measurementTypeDefLookup[(a as Measurement).type]?.isDailyMeasurement ?? false
          : false
        const bIsDailyMeasurement = bIsMeasurement
          ? measurementTypeDefLookup[(b as Measurement).type]?.isDailyMeasurement ?? false
          : false

        // If only one is a daily measurement, move it to the end
        if (aIsDailyMeasurement && !bIsDailyMeasurement) {
          return 1
        }
        if (!aIsDailyMeasurement && bIsDailyMeasurement) {
          return -1
        }

        // If both or neither are daily measurements, maintain original order
        return 0
      })
    })
  }

  return sections
}
